import React, { useEffect, useMemo, useState, useContext } from 'react';
import api from '../api';
import AuthContext from '../AuthContext';
import { useNotification } from '../NotificationContext';
import SkeletonLoader from '../components/SkeletonLoader';
import CategoryManagerModal from '../components/CategoryManagerModal';

const ExpensesPage = () => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';

  // Helper: Get local date string YYYY-MM-DD (not UTC)
  const getLocalDateString = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const todayDate = new Date();
  const todayIso = getLocalDateString(todayDate);
  const firstDayIso = getLocalDateString(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1));
  
  // 7 days ago limit for staff
  const sevenDaysAgoIso = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return getLocalDateString(d);
  }, []);

  const { addNotification } = useNotification();

  // Add-expense modal state
  const [showModal, setShowModal] = useState(false);
  const [expenseDate, setExpenseDate] = useState(todayIso);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [itemName, setItemName] = useState('');
  const [itemAmount, setItemAmount] = useState('');
  const [responsibleEmpId, setResponsibleEmpId] = useState('');
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]); // For dropdown
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // History - default to current month for admin, or last 7 days for staff
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    date_from: isAdmin ? firstDayIso : sevenDaysAgoIso,
    date_to: todayIso
  });
  const [monthFilter, setMonthFilter] = useState('');

  // Keep filters restricted to 7 days for staff whenever role resolves
  useEffect(() => {
    if (!isAdmin) {
      setFilters(prev => ({
        date_from: (!prev.date_from || prev.date_from < sevenDaysAgoIso) ? sevenDaysAgoIso : prev.date_from,
        date_to: (!prev.date_to || prev.date_to > todayIso) ? todayIso : prev.date_to,
      }));
      setMonthFilter('');
    }
  }, [isAdmin, sevenDaysAgoIso, todayIso]);

  // removed bill detail modal (no bill grouping anymore)

  const load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const r = await api.get('/expenses', { params });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      console.error('load expenses failed', err);
      setRows([]);
      addNotification?.(err.response?.data?.message || 'โหลดข้อมูลรายจ่ายไม่สำเร็จ', 'danger');
    } finally { setLoading(false); }
  };

  const loadEmployees = async () => {
    try {
      const res = await api.get('/employees');
      setEmployees(res.data || []);
    } catch (err) {
      console.error('load employees failed', err);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await api.get('/expense-categories?active_only=true');
      setCategories(res.data || []);
    } catch (err) {
      console.error('load categories failed', err);
    }
  };

  useEffect(() => { load(); loadEmployees(); loadCategories(); }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (!isAdmin) {
      if (name === 'date_from' && value < sevenDaysAgoIso) {
        addNotification?.('พนักงานสามารถดูย้อนหลังได้สูงสุด 7 วัน', 'warning');
        setFilters(prev => ({ ...prev, [name]: sevenDaysAgoIso }));
        return;
      }
      if (name === 'date_to' && value > todayIso) {
        setFilters(prev => ({ ...prev, [name]: todayIso }));
        return;
      }
    }
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  const applyFilters = () => load();
  const clearFilters = () => {
    const fromDate = isAdmin ? firstDayIso : sevenDaysAgoIso;
    setFilters({ date_from: fromDate, date_to: todayIso });
    setMonthFilter('');
    setTimeout(load, 50);
  };

  const handleMonthChange = (e) => {
    if (!isAdmin) return;
    const value = e.target.value;
    setMonthFilter(value);
    if (value) {
      // value format: "YYYY-MM"
      const [year, month] = value.split('-');
      const firstDay = `${year}-${month}-01`;
      const lastDay = getLocalDateString(new Date(year, month, 0));
      setFilters({ date_from: firstDay, date_to: lastDay });
      setTimeout(load, 50);
    }
  };

  const handleOpenModal = () => {
    setExpenseDate(todayIso);
    setPaymentMethod('cash');
    setNote('');
    setItemName('');
    setItemAmount('');
    setResponsibleEmpId('');
    setShowModal(true);
  };

  const handleSaveExpense = async () => {
    const amt = parseFloat(String(itemAmount).replace(/,/g, ''));
    if (Number.isNaN(amt) || amt <= 0) {
      addNotification?.('กรุณาระบุจำนวนเงินที่ถูกต้อง', 'warning');
      return;
    }
    if (!itemName.trim()) {
      addNotification?.('กรุณาระบุรายการ', 'warning');
      return;
    }
    if (!isAdmin && expenseDate < sevenDaysAgoIso) {
      addNotification?.('พนักงานไม่สามารถบันทึกรายจ่ายย้อนหลังเกิน 7 วันได้', 'warning');
      return;
    }
    try {
      setSaving(true);
      await api.post('/expenses', {
        expense_date: expenseDate,
        category: itemName.trim(),
        amount: amt,
        payment_method: paymentMethod,
        note: note || null,
        responsible_emp_id: responsibleEmpId || null,
      });
      addNotification?.('บันทึกรายจ่ายเรียบร้อย', 'success');
      setShowModal(false);
      load();
    } catch (err) {
      console.error('save expense failed', err);
      addNotification?.(err.response?.data?.message || 'บันทึกรายจ่ายไม่สำเร็จ', 'danger');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!isAdmin) return;
    if (!window.confirm('ลบรายการนี้?')) return;
    try {
      await api.delete(`/expenses/${id}`);
      addNotification?.('ลบรายการเรียบร้อย', 'success');
      load();
    } catch (err) {
      console.error('delete expense failed', err);
      addNotification?.(err.response?.data?.message || 'ลบรายการไม่สำเร็จ', 'danger');
    }
  };

  const fmt = (v) => (Number(v) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const total = useMemo(() => rows.reduce((s, r) => s + (Number(r.amount) || 0), 0), [rows]);
  const [searchTerm, setSearchTerm] = useState('');
  const filteredRows = useMemo(() => {
    let r = Array.isArray(rows) ? [...rows] : [];
    if (searchTerm.trim()) {
      const t = searchTerm.toLowerCase();
      r = r.filter(x =>
        (x.expense_code && x.expense_code.toLowerCase().includes(t)) ||
        (x.category && x.category.toLowerCase().includes(t)) ||
        (x.note && x.note.toLowerCase().includes(t)) ||
        (x.payment_method && x.payment_method.toLowerCase().includes(t))
      );
    }
    // sort by created_at desc if available
    r.sort((a, b) => new Date(b.created_at || b.expense_date) - new Date(a.created_at || a.expense_date));
    return r;
  }, [rows, searchTerm]);

  // Pagination
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [searchTerm, filters, pageSize]);
  const totalCount = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const pageStart = (page - 1) * pageSize;
  const pageEnd = Math.min(totalCount, pageStart + pageSize);
  const pagedRows = filteredRows.slice(pageStart, pageEnd);

  return (
    <div className="dashboard-page container">
      {/* Hero Header */}
      <div className="dashboard-hero mb-3">
        <div>
          <div className="dashboard-hero-title">
            <i className="bi bi-cash-coin me-2"></i>รายจ่าย
          </div>
          <div className="dashboard-hero-subtitle">บันทึก ติดตาม และจัดการรายจ่ายทั้งหมดของร้าน</div>
        </div>
        <div className="d-flex gap-2">
          {isAdmin && (
            <button className="btn btn-outline-secondary" onClick={() => setShowCategoryModal(true)}>
              <i className="bi bi-folder me-1"></i>จัดการหมวดหมู่
            </button>
          )}
          <button className="btn btn-ks-primary" style={{ color: '#ffffff' }} onClick={handleOpenModal}>
            <i className="bi bi-plus-circle me-1"></i>เพิ่มรายจ่าย
          </button>
        </div>
      </div>

      <div className="card mb-3 shadow-sm">
        <div className="card-body p-3">
          <div className="row g-2 align-items-end">
            {isAdmin && (
              <div className="col-auto">
                <label className="form-label small text-muted mb-1">เดือน</label>
                <input type="month" className="form-control form-control-sm" value={monthFilter} onChange={handleMonthChange} />
              </div>
            )}
            <div className="col-auto">
              <label className="form-label small text-muted mb-1">จากวันที่</label>
              <input
                type="date"
                name="date_from"
                value={filters.date_from}
                min={isAdmin ? undefined : sevenDaysAgoIso}
                max={todayIso}
                onChange={handleFilterChange}
                className="form-control form-control-sm"
              />
            </div>
            <div className="col-auto">
              <label className="form-label small text-muted mb-1">ถึงวันที่</label>
              <input
                type="date"
                name="date_to"
                value={filters.date_to}
                min={isAdmin ? undefined : sevenDaysAgoIso}
                max={todayIso}
                onChange={handleFilterChange}
                className="form-control form-control-sm"
              />
            </div>
            <div className="col-auto">
              <button className="btn btn-sm btn-secondary me-1" onClick={applyFilters} disabled={loading}>
                <i className="bi bi-filter me-1"></i>กรอง
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters} disabled={loading}>
                <i className="bi bi-x-circle me-1"></i>ล้าง
              </button>
            </div>
            <div className="col ms-auto">
              <label className="form-label small text-muted mb-1">ค้นหา</label>
              <input type="text" className="form-control form-control-sm" placeholder="ค้นหารายการ..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
          </div>
          {!isAdmin && (
            <div className="mt-2 text-muted small">
              <i className="bi bi-clock-history text-info me-1"></i>
              สิทธิ์พนักงาน: ดูข้อมูลย้อนหลังได้สูงสุดไม่เกิน 7 วัน ({sevenDaysAgoIso} ถึง {todayIso})
            </div>
          )}
        </div>
      </div>

      {loading ? <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary"></div></div> : null}

      <div className="card shadow-sm">
        <div className="card-body p-0">

          {
            loading ? (
              <SkeletonLoader type="table" rows={5} columns={6} />
            ) : filteredRows.length === 0 ? (
              <div className="text-center text-muted py-4">ไม่พบข้อมูลรายจ่ายในช่วงวันที่เลือก</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light text-muted">
                    <tr>
                      <th style={{ width: '140px' }}>รหัส</th>
                      <th style={{ width: '120px' }}>วัน/เวลา</th>
                      <th style={{ width: '100px' }}>วิธีชำระ</th>
                      <th style={{ width: '150px' }}>หมวดหมู่</th>
                      <th className="text-end" style={{ width: '80px' }}>ยอด</th>
                      <th style={{ width: '150px' }}>ผู้รับผิดชอบ</th>
                      <th style={{ width: '220px' }}>หมายเหตุ</th>
                      {isAdmin && <th style={{ width: '60px' }}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row) => {
                      const dt = row.created_at ? new Date(row.created_at) : null;
                      const dtStr = dt ? `${dt.toLocaleDateString('th-TH')} ${dt.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}` : (row.expense_date || '-');
                      return (
                        <tr key={row.id}>
                          <td className="text-muted small align-top font-monospace">{row.expense_code || '-'}</td>
                          <td className="text-muted small align-top">{dtStr}</td>
                          <td className="align-top">
                            <span className={`badge ${row.payment_method === 'transfer' ? 'text-dark' : 'bg-secondary text-white'}`} style={row.payment_method === 'transfer' ? { backgroundColor: '#f3e5ab' } : undefined}>
                              {row.payment_method === 'transfer' ? 'โอน' : 'เงินสด'}
                            </span>
                          </td>
                          <td className="align-top fw-semibold">{row.category || '-'}</td>
                          <td className="text-end fw-bold text-danger align-top">{fmt(row.amount)} ฿</td>
                          <td className="text-muted small align-top">{row.responsible_name || '-'}</td>
                          <td className="text-muted small align-top">{row.note || '-'}</td>
                          {isAdmin && (
                            <td className="text-end align-top">
                              <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row.id)} title="ลบ">
                                <i className="bi bi-trash"></i>
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="fw-bold bg-light">
                      <td colSpan={3} className="text-end">รวมทั้งหมด</td>
                      <td className="text-end text-danger">{fmt(total)} ฿</td>
                      <td colSpan={isAdmin ? 3 : 2}></td>
                    </tr>
                  </tfoot>
                </table>
                <div className="d-flex justify-content-between align-items-center p-3">
                  <div className="text-muted small">แสดง {totalCount === 0 ? 0 : pageStart + 1}-{pageEnd} จาก {totalCount} รายการ</div>
                  <div className="btn-group">
                    <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>ก่อนหน้า</button>
                    <span className="btn btn-sm btn-light disabled">หน้า {page}/{totalPages}</span>
                    <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>ถัดไป</button>
                  </div>
                </div>
              </div>
            )}
        </div >
      </div >

      {/* Add Expense Modal */}
      {
        showModal && (
          <>
            <div className="modal show d-block" tabIndex="-1">
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header bg-success text-white">
                    <h5 className="modal-title"><i className="bi bi-receipt me-2"></i>เพิ่มรายจ่าย</h5>
                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)} disabled={saving}></button>
                  </div>
                  <div className="modal-body">
                    {/* แถว 1: วันที่ + วิธีชำระ */}
                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <label className="form-label fw-bold">วันที่</label>
                        <input
                          type="date"
                          className="form-control"
                          value={expenseDate}
                          min={isAdmin ? undefined : sevenDaysAgoIso}
                          max={todayIso}
                          onChange={e => setExpenseDate(e.target.value)}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">วิธีชำระ</label>
                        <select className="form-select" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                          <option value="cash">เงินสด</option>
                          <option value="transfer">โอน</option>
                        </select>
                      </div>
                    </div>

                    {/* แถว 2: รายการ + จำนวนเงิน */}
                    <div className="row g-3 mb-3">
                      <div className="col-md-8">
                        <label className="form-label fw-bold">รายการ / หมวดหมู่</label>
                        <select
                          className="form-select"
                          value={itemName}
                          onChange={e => setItemName(e.target.value)}
                        >
                          <option value="">-- เลือกหมวดหมู่ --</option>
                          {categories.map(c => (
                            <option key={c.id} value={c.name}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">จำนวนเงิน (฿)</label>
                        <input type="number" min="0" step="0.01" className="form-control" value={itemAmount} onChange={e => setItemAmount(e.target.value)} placeholder="0.00" />
                      </div>
                    </div>

                    {/* แถว 3: ผู้รับผิดชอบ + หมายเหตุ */}
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-bold">ผู้รับผิดชอบ</label>
                        <select className="form-select" value={responsibleEmpId} onChange={e => setResponsibleEmpId(e.target.value)}>
                          <option value="">-- ผู้ทำรายการ (ฉัน) --</option>
                          {employees.filter(e => e.is_active).map(e => (
                            <option key={e.emp_id} value={e.emp_id}>{e.emp_name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">หมายเหตุ</label>
                        <input type="text" className="form-control" value={note} onChange={e => setNote(e.target.value)} placeholder="รายละเอียดเพิ่มเติม..." />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>
                      <i className="bi bi-x-circle me-1"></i>ยกเลิก
                    </button>
                    <button type="button" className="btn text-white" style={{ backgroundColor: '#d4af37', border: 'none' }} onClick={handleSaveExpense} disabled={saving}>
                      {saving ? (<><span className="spinner-border spinner-border-sm me-2"></span>กำลังบันทึก...</>) : (<><i className="bi bi-check-circle me-1"></i>บันทึกรายจ่าย</>)}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop show"></div>
          </>
        )
      }

      <CategoryManagerModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        onUpdate={() => { loadCategories(); }}
      />
    </div >
  );
};

export default ExpensesPage;
